import React, { useState, useRef, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { closeOutline, navigateOutline } from 'ionicons/icons';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { EventWithDistance, formatDistance, openInMaps } from '../services/searchService';
import { useTranslation } from '../hooks/useTranslation';
import './PublicEventDetailModal.css';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const MapSlide: React.FC<{ lat: number; lng: number; address: string }> = ({ lat, lng, address }) => {
  const [mapKey] = useState(() => Date.now());
  const MapFix = () => {
    const map = useMap();
    useEffect(() => {
      const t = setTimeout(() => { try { map.invalidateSize(); } catch (e) { /* */ } }, 300);
      return () => clearTimeout(t);
    }, [map]);
    return null;
  };
  return (
    <div className="pub-carousel-map">
      <MapContainer key={mapKey} center={[lat, lng]} zoom={15}
        style={{ width: '100%', height: '100%' }} scrollWheelZoom={false} zoomControl={false}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Marker position={[lat, lng]} />
        <MapFix />
      </MapContainer>
      <button className="pub-map-open-btn" onClick={() => openInMaps(lat, lng, address)}>
        <IonIcon icon={navigateOutline} />
        {address}
      </button>
    </div>
  );
};

interface Props { event: EventWithDistance; onClose: () => void; }

const PublicEventDetailModal: React.FC<Props> = ({ event, onClose }) => {
  // language viene del hook arriba
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const touchStartX = useRef(0);
  const { t, language } = useTranslation();

  const hasLocation = event.lat !== 0 && event.long !== 0;

  const slides: { type: 'image' | 'video' | 'map'; url?: string }[] = [
    ...(event.photoItem     ? [{ type: 'image' as const, url: event.photoItem }]     : []),
    ...(event.photoPortrait ? [{ type: 'image' as const, url: event.photoPortrait }] : []),
    ...(event.videoUrl      ? [{ type: 'video' as const, url: event.videoUrl }]      : []),
    ...(hasLocation         ? [{ type: 'map'   as const }]                            : []),
  ];

  const locale = language === 'pt' ? 'pt-BR' : language === 'en' ? 'en-US' : 'es-GT';

  const formatDate = (ts: any): string => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const videoSlideIdx = slides.findIndex(s => s.type === 'video');

  const goTo = (idx: number) => {
    setActiveSlide(Math.max(0, Math.min(idx, slides.length - 1)));
  };

  // Auto-play al entrar al slide de video, pausa al salir
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (activeSlide === videoSlideIdx) {
      video.play().catch(() => { /* autoplay bloqueado por browser */ });
    } else {
      video.pause();
    }
  }, [activeSlide, videoSlideIdx]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) goTo(activeSlide + (diff > 0 ? 1 : -1));
  };

  return (
    <div className="pub-modal-overlay" onClick={onClose}>
      <div className="pub-modal" onClick={e => e.stopPropagation()}>

        {/* Carrusel */}
        <div className="pub-carousel-wrap">
          <div
            className="pub-carousel"
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="pub-carousel-track"
              style={{ transform: `translateX(-${activeSlide * 100}%)` }}
            >
              {slides.map((slide, i) => (
                <div key={i} className="pub-carousel-slide">
                  {slide.type === 'image' && <img src={slide.url} alt="" className="pub-carousel-media" />}
                  {slide.type === 'video' && (
                    <video
                      ref={videoRef}
                      src={slide.url}
                      className="pub-carousel-media"
                      controls
                      playsInline
                      preload="metadata"
                    />
                  )}
                  {slide.type === 'map' && i === activeSlide && <MapSlide lat={event.lat} lng={event.long} address={event.address_id} />}
                </div>
              ))}
            </div>
          </div>

          <button className="pub-carousel-close" onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </button>

          {slides.length > 1 && activeSlide > 0 && (
            <button className="pub-carousel-arrow pub-carousel-arrow--left" onClick={() => goTo(activeSlide - 1)}>‹</button>
          )}
          {slides.length > 1 && activeSlide < slides.length - 1 && (
            <button className="pub-carousel-arrow pub-carousel-arrow--right" onClick={() => goTo(activeSlide + 1)}>›</button>
          )}

          {slides.length > 1 && (
            <div className="pub-carousel-dots">
              {slides.map((_, i) => (
                <span key={i} className={`pub-dot${i === activeSlide ? ' pub-dot--active' : ''}`} />
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="pub-modal-body">
          <h2 className="pub-modal-title">{event.information}</h2>
          <div className="pub-modal-details">
            <div className="pub-detail-row">
              {t('event.datetime_format', {
                date: formatDate(event.date),
                start: event.schedule_start,
                end: event.schedule_finish,
              })}
            </div>
            <div className="pub-detail-row">
              {event.address_id},{' '}
              {event.price === 0
                ? t('event.free_entry')
                : `${t('event.value_label')} ${event.currency ?? ''}${Number(event.price).toFixed(2)}`}
              {event.distanceKm > 0 && ` ${t('event.distance_label')} ${formatDistance(event.distanceKm)}`}
            </div>
          </div>
          {event.information2 && (
            <p className="pub-modal-desc">{event.information2}</p>
          )}
        </div>

      </div>
    </div>
  );
};

export default PublicEventDetailModal;
